import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';

/**
 * Sandbox restriction placeholder.
 *
 * Shown when a sandbox user navigates to a route that's not in the demo
 * allowlist. The route guard in App.js renders this *instead of* the real
 * page, so the protected component never mounts and there is no DOM tree
 * to remove via DevTools.
 *
 * Design intent: the demo should *look* like the full product (sidebar
 * shows every menu item) but pages outside the demo flow display this clean
 * "available in full Keystone" placeholder instead of redirecting away.
 */
export default function SandboxRestricted() {
  const location = useLocation();

  // Pretty-print the page name from the path
  const prettyName = (() => {
    const seg = location.pathname.split('/').filter(Boolean).pop() || 'this page';
    return seg
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  })();

  return (
    <div data-testid="sandbox-restricted" className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full">
        {/* Glyph */}
        <div className="flex items-center justify-center mb-8">
          <div className="relative">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)',
                boxShadow: '0 8px 24px rgba(233, 30, 99, 0.18)',
              }}
            >
              <Lock className="w-10 h-10" style={{ color: '#E91E63' }} />
            </div>
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mb-8">
          <div
            className="inline-block text-[10.5px] font-bold tracking-widest uppercase mb-3"
            style={{ color: '#E91E63', letterSpacing: '0.22em' }}
          >
            Public Sandbox · Demo Mode
          </div>
          <h1
            className="font-bold leading-tight tracking-tight"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(28px, 4vw, 36px)',
              color: '#0A2540',
              letterSpacing: '-0.02em',
            }}
          >
            <span className="font-medium" style={{ color: '#0A2540' }}>{prettyName}</span> isn't part<br/>
            of this demo experience.
          </h1>
          <p className="mt-5 text-base leading-relaxed" style={{ color: '#4A5568' }}>
            The Public Sandbox lets prospects test the daily operational flow,
            sales orders, dispatches, customer code mapping and traceability.
            This page is <em>fully available in the live Keystone product</em>,
            but is hidden in the demo so you can stay focused on what matters.
          </p>
        </div>

        {/* Actions, back to demo flow */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/sales-order/new"
            className="group flex items-center justify-between px-5 py-4 rounded-lg transition-all"
            style={{
              background: '#0A2540',
              color: '#fff',
              boxShadow: '0 4px 14px rgba(10, 37, 64, 0.18)',
            }}
            data-testid="sandbox-back-to-sales-order"
          >
            <div>
              <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-70" style={{ letterSpacing: '0.18em' }}>Continue the demo</div>
              <div className="text-base font-semibold mt-0.5">Create a Sales Order</div>
            </div>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/dispatches"
            className="group flex items-center justify-between px-5 py-4 rounded-lg transition-all border"
            style={{
              background: '#FAF8F5',
              borderColor: '#E2E8F0',
              color: '#0A2540',
            }}
            data-testid="sandbox-back-to-dispatches"
          >
            <div>
              <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-60" style={{ color: '#4A5568', letterSpacing: '0.18em' }}>See live data</div>
              <div className="text-base font-semibold mt-0.5">Browse Dispatches</div>
            </div>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Talk to us */}
        <div
          className="mt-8 p-5 rounded-lg text-center"
          style={{
            background: 'linear-gradient(135deg, #FCE7F3 0%, #FFF1F8 100%)',
            border: '1px solid rgba(233, 30, 99, 0.18)',
          }}
        >
          <div className="text-sm" style={{ color: '#0A2540' }}>
            Want to see <strong>{prettyName}</strong> with your own data?{' '}
            <Link
              to="/site/contact"
              className="font-bold underline"
              style={{ color: '#E91E63' }}
              data-testid="sandbox-talk-to-us"
            >
              Talk to us about a real account →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
